import { Tabs as TabsPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '@/shared/lib';

/**
 * Вкладки-пилюли карточки (README §5): контейнер padding 3, radius 16, фон surface-sunken,
 * border line-hair; вкладка 28, padding 0 12, radius 13, 12 px; активная — белая, 600, тень сегмента;
 * счётчик 10.5 tabular-nums (активная — gold-leaf-3, иначе ink-400).
 *
 * variant="toolbar" — вкладки в панели над таблицей экрана (README §7 «Уведомления»): контейнер
 * padding 2, border line-strong; вкладка 26, padding 0 10, radius 9, gap 6; счётчик 11 600.
 */
export type TabsVariant = 'card' | 'toolbar';

export const Tabs = TabsPrimitive.Root;
export const TabsContent = TabsPrimitive.Content;

export interface TabsListProps extends ComponentProps<typeof TabsPrimitive.List> {
  variant?: TabsVariant;
}

export function TabsList({ className, variant = 'card', ...props }: TabsListProps) {
  return (
    <TabsPrimitive.List
      className={cn(
        'flex w-fit max-w-full items-center gap-2 self-start overflow-x-auto rounded-16 border border-line-hair bg-surface-sunken p-3',
        variant === 'toolbar' && 'self-auto border-line-strong p-2',
        className,
      )}
      {...props}
    />
  );
}

export interface TabsTriggerProps extends ComponentProps<typeof TabsPrimitive.Trigger> {
  count?: number | string;
  variant?: TabsVariant;
}

export function TabsTrigger({
  className,
  count,
  variant = 'card',
  children,
  ...props
}: TabsTriggerProps) {
  const toolbar = variant === 'toolbar';
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'group flex h-28 shrink-0 cursor-pointer items-center gap-5 rounded-13 px-12 text-[12px] font-medium whitespace-nowrap text-ink-600',
        'hover:bg-paper-deep data-[state=active]:bg-paper-pure data-[state=active]:font-semibold data-[state=active]:text-ink-900',
        'data-[state=active]:shadow-segment-on data-[state=active]:hover:bg-paper-pure',
        toolbar && 'h-26 gap-6 rounded-9 px-10',
        className,
      )}
      {...props}
    >
      {children}
      {count !== undefined && count !== '' ? (
        <span
          className={cn(
            'text-[10.5px] font-normal text-ink-400 tabular group-data-[state=active]:text-gold-leaf-3',
            toolbar && 'text-[11px] font-semibold',
          )}
        >
          {count}
        </span>
      ) : null}
    </TabsPrimitive.Trigger>
  );
}
